import type { MouseEvent as ReactMouseEvent } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface SearchSelectOption {
  value: string;
  label: string;
  hint?: string;
  /** Optional thumbnail URL (product image, etc.). */
  image?: string | null;
}

function OptionThumb({ src }: { src?: string | null }) {
  if (!src) {
    return (
      <span
        className="h-8 w-8 shrink-0 rounded-md border border-line bg-input-bg"
        aria-hidden
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      className="h-8 w-8 shrink-0 rounded-md border border-line bg-input-bg object-cover"
      loading="lazy"
    />
  );
}

const VIEWPORT_PAD = 8;
const PANEL_MAX_H = 280;

function computePanelPosition(trigger: HTMLElement, panelHeight: number): { top: number; left: number; width: number } {
  const rect = trigger.getBoundingClientRect();
  const width = rect.width;
  const gap = 4;
  let top = rect.bottom + gap;
  if (top + panelHeight > globalThis.innerHeight - VIEWPORT_PAD) {
    top = Math.max(VIEWPORT_PAD, rect.top - gap - panelHeight);
  }
  let left = rect.left;
  left = Math.max(VIEWPORT_PAD, Math.min(left, globalThis.innerWidth - width - VIEWPORT_PAD));
  return { top, left, width };
}

type SearchSelectBase = {
  label?: string;
  hint?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  options: SearchSelectOption[];
  disabled?: boolean;
  emptyMessage?: string;
  className?: string;
  /** Opens the dropdown and focuses the search field (e.g. command-palette / queue entry). */
  autoOpenSearch?: boolean;
  onAutoOpenSearchHandled?: () => void;
  /** When set, search is also forwarded for remote filtering (parent refetches options). */
  onSearchChange?: (query: string) => void;
  /** Skip client-side filtering when options are already filtered by the server. */
  serverFiltered?: boolean;
  /** Infinite scroll: load next page when the list nears the bottom. */
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
};

type SearchSelectSingle = SearchSelectBase & {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
};

type SearchSelectMulti = SearchSelectBase & {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
  maxSelections?: number;
};

export type SearchSelectProps = SearchSelectSingle | SearchSelectMulti;

export function SearchSelect(props: SearchSelectProps) {
  const {
    label,
    hint,
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    options,
    disabled = false,
    emptyMessage = "No matches",
    className = "",
    autoOpenSearch = false,
    onAutoOpenSearchHandled,
    onSearchChange,
    serverFiltered = false,
    onLoadMore,
    hasMore = false,
    loadingMore = false,
  } = props;

  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedValues = props.multiple ? props.value : props.value ? [props.value] : [];
  const optionByValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);

  const filtered = useMemo(() => {
    if (serverFiltered) return options;
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.hint?.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, search, serverFiltered]);

  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const h = panelRef.current?.offsetHeight ?? PANEL_MAX_H;
    setPanelPos(computePanelPosition(triggerRef.current, h));
  }, []);

  useEffect(() => {
    if (!autoOpenSearch || disabled) return;
    setOpen(true);
    const t = globalThis.setTimeout(() => {
      searchRef.current?.focus();
      searchRef.current?.select();
      onAutoOpenSearchHandled?.();
    }, 0);
    return () => globalThis.clearTimeout(t);
  }, [autoOpenSearch, disabled, onAutoOpenSearchHandled]);

  useEffect(() => {
    if (!open) {
      setPanelPos(null);
      setSearch((prev) => {
        if (prev) onSearchChange?.("");
        return "";
      });
      setActiveIndex(0);
      return;
    }
    reposition();
    const t = globalThis.setTimeout(() => searchRef.current?.focus(), 0);
    globalThis.addEventListener("scroll", reposition, true);
    globalThis.addEventListener("resize", reposition);
    return () => {
      globalThis.clearTimeout(t);
      globalThis.removeEventListener("scroll", reposition, true);
      globalThis.removeEventListener("resize", reposition);
    };
  }, [open, reposition, onSearchChange]);

  useEffect(() => {
    if (!open) return;
    reposition();
  }, [open, filtered.length, reposition]);

  const maybeLoadMore = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasMore || loadingMore || !onLoadMore) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 64) onLoadMore();
  }, [hasMore, loadingMore, onLoadMore]);

  useEffect(() => {
    if (!open) return;
    maybeLoadMore();
  }, [open, filtered.length, maybeLoadMore]);

  useEffect(() => {
    if (!open) return;
    const ro = new ResizeObserver(reposition);
    if (panelRef.current) ro.observe(panelRef.current);
    return () => ro.disconnect();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function toggleOption(value: string) {
    if (props.multiple) {
      const max = props.maxSelections ?? 0;
      const has = props.value.includes(value);
      if (has) {
        props.onChange(props.value.filter((v) => v !== value));
      } else if (max > 0 && props.value.length >= max) {
        return;
      } else {
        props.onChange([...props.value, value]);
      }
      return;
    }
    props.onChange(value);
    setOpen(false);
  }

  function removeChip(value: string, e: ReactMouseEvent) {
    e.stopPropagation();
    if (!props.multiple) return;
    props.onChange(props.value.filter((v) => v !== value));
  }

  function onTriggerKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((v) => !v);
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
    }
  }

  function onSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    }
    if (e.key === "Enter" && filtered[activeIndex]) {
      e.preventDefault();
      toggleOption(filtered[activeIndex]!.value);
    }
  }

  const singleSelected = !props.multiple && props.value ? optionByValue.get(props.value) : undefined;
  const singleSelectedLabel = singleSelected?.label ?? (!props.multiple && props.value ? props.value : null);

  const triggerLabel = props.multiple ? (
    props.value.length === 0 ? (
      <span className="text-muted-2">{placeholder}</span>
    ) : (
      <span className="flex flex-wrap gap-1">
        {props.value.map((v) => {
          const opt = optionByValue.get(v);
          return (
            <span
              key={v}
              className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-brand-pink/15 px-1.5 py-0.5 text-[11px] text-foreground"
            >
              {opt?.image ? (
                <img src={opt.image} alt="" className="h-4 w-4 shrink-0 rounded object-cover" />
              ) : null}
              <span className="truncate">{opt?.label ?? v}</span>
              {!disabled && (
                <button
                  type="button"
                  className="shrink-0 rounded p-0.5 hover:bg-hover"
                  aria-label={`Remove ${opt?.label ?? v}`}
                  onClick={(e) => removeChip(v, e)}
                >
                  <X size={10} />
                </button>
              )}
            </span>
          );
        })}
      </span>
    )
  ) : (
    <span className={`flex min-w-0 items-center gap-2 ${props.value ? "text-foreground" : "text-muted-2"}`}>
      {singleSelected && singleSelected.image !== undefined ? (
        singleSelected.image ? (
          <img
            src={singleSelected.image}
            alt=""
            className="h-7 w-7 shrink-0 rounded-md border border-line object-cover"
          />
        ) : (
          <span className="h-7 w-7 shrink-0 rounded-md border border-line bg-input-bg" aria-hidden />
        )
      ) : null}
      <span className="min-w-0 truncate">{singleSelectedLabel ?? placeholder}</span>
    </span>
  );

  const panel =
    open && panelPos
      ? createPortal(
          <div
            ref={panelRef}
            role="listbox"
            id={listId}
            aria-multiselectable={props.multiple || undefined}
            className="fixed z-[10000] overflow-hidden rounded-xl border border-line bg-surface shadow-xl shadow-black/20"
            style={{ top: panelPos.top, left: panelPos.left, width: panelPos.width, maxHeight: PANEL_MAX_H }}
          >
            <div className="border-b border-line p-2">
              <div className="relative">
                <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-2" />
                <input
                  ref={searchRef}
                  type="search"
                  value={search}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSearch(next);
                    setActiveIndex(0);
                    onSearchChange?.(next);
                  }}
                  onKeyDown={onSearchKeyDown}
                  placeholder={searchPlaceholder}
                  className="w-full rounded-lg border border-line bg-input-bg py-1.5 pl-8 pr-2 text-xs text-foreground placeholder:text-muted-2 outline-none focus:border-brand-pink/60"
                  aria-label={searchPlaceholder}
                />
              </div>
            </div>
            <ul
              ref={listRef}
              className="max-h-52 overflow-y-auto p-1"
              role="presentation"
              onScroll={maybeLoadMore}
            >
              {filtered.length === 0 ? (
                <li className="px-2 py-3 text-center text-xs text-muted-2">
                  {loadingMore ? "Loading…" : emptyMessage}
                </li>
              ) : (
                filtered.map((opt, idx) => {
                  const selected = selectedValues.includes(opt.value);
                  const active = idx === activeIndex;
                  return (
                    <li key={opt.value} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left text-xs transition-colors ${
                          active ? "bg-brand-pink/15 text-foreground" : "text-foreground/85 hover:bg-hover"
                        }`}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => toggleOption(opt.value)}
                      >
                        <span
                          className={`mt-1.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            selected ? "border-brand-pink bg-brand-pink text-white" : "border-line bg-input-bg"
                          } ${props.multiple ? "" : "rounded-full"}`}
                        >
                          {selected && <Check size={10} strokeWidth={3} />}
                        </span>
                        {opt.image !== undefined ? <OptionThumb src={opt.image} /> : null}
                        <span className="min-w-0 flex-1">
                          <span className="block whitespace-normal break-words font-medium leading-snug">{opt.label}</span>
                          {opt.hint && (
                            <span className="mt-0.5 block whitespace-normal break-words text-[10px] leading-snug text-muted-2">
                              {opt.hint}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
              {(hasMore || loadingMore) && filtered.length > 0 && (
                <li className="px-2 py-2 text-center text-[10px] text-muted-2">
                  {loadingMore ? "Loading more…" : "Scroll for more"}
                </li>
              )}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={`block ${className}`}>
      {label ? (
        <span className="mb-1 block min-h-4 whitespace-nowrap text-xs font-medium leading-4 text-muted">{label}</span>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        title={singleSelectedLabel ?? undefined}
        className={`flex min-h-[38px] w-full items-center justify-between gap-2 rounded-xl border border-line bg-input-bg px-3 py-2 text-left text-sm outline-none transition-colors focus:border-brand-pink/60 disabled:cursor-not-allowed disabled:opacity-50 ${
          open ? "border-brand-pink/60" : ""
        }`}
      >
        <span className="min-w-0 flex-1 overflow-hidden">{triggerLabel}</span>
        <ChevronDown size={16} className={`shrink-0 text-muted-2 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <span
        className={`mt-1 block min-h-[14px] text-[10px] leading-[14px] ${hint ? "text-muted-2" : "invisible"}`}
        aria-hidden={!hint}
      >
        {hint ?? "\u00a0"}
      </span>
      {panel}
    </div>
  );
}
